import axios from 'axios';
import {
  Button,
  Input,
  message,
  Page,
  Space,
  Text,
  Checkbox,
  Tooltip,
  Icon,
} from 'components';
import { AuthContext } from 'contexts';
import React from 'react';
import styled from 'styled-components';
import { OBJECT_ID_REGEX } from 'utils/constants';

const API_URL = '/user/qa_page/force_verify';

const buildUrl = (orgId?: string, overrideThreshold?: boolean) => {
  if (orgId) {
    const searchParams = new URLSearchParams({
      'orgIds[]': orgId.trim(),
      ...(overrideThreshold ? { overrideThreshold: 'true' } : {}),
    });
    return `${API_URL}?${searchParams.toString()}`;
  }
  return API_URL;
};

const buildFullUrl = (orgId?: string, overrideThreshold?: boolean) => `${window.location.host}${buildUrl(orgId, overrideThreshold)}`;

const ForceVerifyPage = () => {
  const { auth } = React.useContext(AuthContext);
  const [isLoading, setIsLoading] = React.useState(false);
  const [orgId, setOrgId] = React.useState(auth.org.id || '');
  const [results, setResults] = React.useState<any[]>([]);
  const [overrideThreshold, setOverrideThreshold] = React.useState(false);

  const fire = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      if (orgId) {
        const trimmedId = orgId.trim();
        if (!trimmedId.match(OBJECT_ID_REGEX)) {
          throw new Error(`${trimmedId} is not a valid object id`);
        }
      }
      const url = buildUrl(orgId, overrideThreshold);
      setIsLoading(true);
      const result = await axios.get(url);
      setResults(prev => [result.data, ...prev]);
    } catch (error) {
      message.error(error?.toString(), 8);
    } finally {
      setIsLoading(false);
    }
  };

  const fullUrl = buildFullUrl(orgId, overrideThreshold);

  return (
    <Page type="filled" title="Verify User QA Page">
      <Layout>
        <Text>
          This will trigger the cron job responsible for verifying/unverifying
          agents
        </Text>
        <Text>
          A single org id can be specified (optional but recommended) to limit
          the cron to one org
        </Text>
        <form onSubmit={fire}>
          <StyledInput
            addonAfter={
              <Button
                disabled={isLoading}
                loading={isLoading}
                htmlType="submit"
              >
                Trigger cron job
              </Button>
            }
            defaultValue={orgId}
            onChange={e => setOrgId(e.target.value)}
            placeholder="Org Id"
          />
          <Checkbox
            checked={overrideThreshold}
            onChange={e => setOverrideThreshold(e.target.checked)}
          >
            <Tooltip title="For safety, the verification cron will abort if more than a certain threshold percentage of users would become unverified. An alert is sent to the brokerbay-prod slack channel for these cases. You will see this if the 'failedOrgs' array is populated. For testing purposes this can be overridden">
              Override threshold <Icon type="antdInfoCircleFilled" />
            </Tooltip>
          </Checkbox>
        </form>
        <Space>
          <Text>API route: {fullUrl}</Text>
          <Button onClick={() => navigator.clipboard.writeText(fullUrl)}>
            Copy Url
          </Button>
        </Space>
        {(results.length || null) && (
          <>
            Result Log:
            <pre>{JSON.stringify(results, null, 2)}</pre>
          </>
        )}
      </Layout>
    </Page>
  );
};

export default ForceVerifyPage;

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 10px;
  padding: 20px;
  background: white;
`;

const StyledInput = styled(Input)`
  width: 500px;
`;
